import React from 'react';
import {
  Box,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCount, formatRate, formatUtcStamp, funnelWithDrop, identityTitle, shortText } from './fanScanUtils';

function SummaryCard({ label, value, hint }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={700}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

function ChartFrame({ title, hint, children }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {hint}
      </Typography>
      {children}
    </Paper>
  );
}

function FanScanOverview({ dashboard }) {
  const totals = dashboard?.totals || {};
  const identities = dashboard?.identities || [];
  const funnel = funnelWithDrop(dashboard?.funnel, totals.started);
  const days = dashboard?.completions_by_day || [];
  const bundle = dashboard?.bundle;
  const identityChart = identities.map((identity) => ({
    ...identity,
    label: `${identity.icon || ''} ${identityTitle(identity)}`.trim(),
  }));
  const funnelChart = funnel.map((row) => ({
    ...row,
    label: shortText(row.title, 28),
  }));

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <SummaryCard label="Started" value={formatCount(totals.started)} hint="Created in range" />
        </Grid>
        <Grid item xs={6} md={2}>
          <SummaryCard label="In progress" value={formatCount(totals.in_progress)} />
        </Grid>
        <Grid item xs={6} md={2}>
          <SummaryCard label="Completed" value={formatCount(totals.completed)} />
        </Grid>
        <Grid item xs={6} md={2}>
          <SummaryCard
            label="Completion"
            value={formatRate(totals.completion_rate)}
            hint="Completed / started"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <SummaryCard label="Guests" value={formatCount(totals.guests)} />
        </Grid>
        <Grid item xs={6} md={2}>
          <SummaryCard label="Accounts" value={formatCount(totals.accounts)} />
        </Grid>
      </Grid>

      {bundle ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Chip size="small" label={bundle.bundle_version || 'Bundle'} />
            <Chip size="small" label={bundle.status || 'unknown'} color={bundle.status === 'draft' ? 'default' : 'warning'} />
            <Chip
              size="small"
              label={bundle.edit_source === 'admin' ? 'Edited in admin' : 'Copy from code'}
            />
            <Chip size="small" label={`${formatCount(bundle.question_count)} questions`} />
            <Typography variant="body2" color="text.secondary">
              {bundle.edited_at
                ? `Last question save ${formatUtcStamp(bundle.edited_at)}`
                : 'No question has been saved in admin yet.'}
            </Typography>
          </Box>
        </Paper>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <ChartFrame
            title="Identities"
            hint="All identities, including zeros. The title is the headline. The emoji stays as it is. Counted when the result finished."
          >
            <Box sx={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <BarChart data={identityChart} layout="vertical" margin={{ left: 8, right: 32 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0f766e" name="Finished">
                    <LabelList dataKey="count" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <TableContainer>
              <Table size="small" aria-label="Identity counts">
                <TableHead>
                  <TableRow>
                    <TableCell>Identity</TableCell>
                    <TableCell align="right">Finished</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {identities.map((identity) => (
                    <TableRow key={identity.id}>
                      <TableCell>
                        {identity.icon ? `${identity.icon} ` : ''}
                        {identityTitle(identity)}
                      </TableCell>
                      <TableCell align="right">{formatCount(identity.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartFrame>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartFrame
            title="Question funnel"
            hint="Sessions created in this range, and how many of them answered each question."
          >
            <Box sx={{ width: '100%', height: 380 }}>
              <ResponsiveContainer>
                <BarChart data={funnelChart} layout="vertical" margin={{ left: 8, right: 32 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="answered" fill="#1d4ed8" name="Answered">
                    <LabelList dataKey="answered" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <TableContainer>
              <Table size="small" aria-label="Question funnel">
                <TableHead>
                  <TableRow>
                    <TableCell>Question</TableCell>
                    <TableCell align="right">Answered</TableCell>
                    <TableCell>Drop-off</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {funnel.map((row) => (
                    <TableRow key={row.question_id}>
                      <TableCell>
                        {row.order}. {row.title}
                      </TableCell>
                      <TableCell align="right">{formatCount(row.answered)}</TableCell>
                      <TableCell>
                        {row.dropFromPrevious === 0
                          ? 'No drop-off'
                          : row.comparedWith === 'started'
                            ? `${formatCount(row.dropFromPrevious)} did not answer`
                            : `${formatCount(row.dropFromPrevious)} fewer than the previous question`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartFrame>
        </Grid>

        <Grid item xs={12}>
          <ChartFrame
            title="Completions by day"
            hint="One row per day in the range, including zeros. Counted on the UTC day the result finished."
          >
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={days} margin={{ left: 0, right: 8, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" angle={-35} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} width={36} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#b45309" name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <TableContainer sx={{ maxHeight: 240 }}>
              <Table size="small" stickyHeader aria-label="Completions by day">
                <TableHead>
                  <TableRow>
                    <TableCell>Day (UTC)</TableCell>
                    <TableCell align="right">Completed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {days.map((row) => (
                    <TableRow key={row.day}>
                      <TableCell>{row.day}</TableCell>
                      <TableCell align="right">{formatCount(row.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartFrame>
        </Grid>
      </Grid>
    </Box>
  );
}

export default FanScanOverview;
